/**
 * Ordering for the QuickBooks invoice list.
 *
 * QuickBooks returns invoices in the order its query happened to produce, which
 * is not chronological — so the most recent invoice, the one the person opening
 * the tab is almost always looking for, could sit anywhere in the list (#843).
 *
 * Kept out of the hook so the ordering itself is testable without React or a
 * QuickBooks connection.
 */

/** An invoice as the QuickBooks hooks hand it back: a wrapper around raw QBO JSON. */
export interface InvoiceListItem {
  data?: Record<string, unknown>;
}

/**
 * Field names carrying a usable date, in preference order. QuickBooks answers
 * in PascalCase; some of our own mappers lowercase the first letter, so both
 * spellings are accepted rather than trusting one.
 */
const DATE_FIELDS = ["TxnDate", "txnDate", "DueDate", "dueDate"] as const;

/**
 * Sort key for one invoice: epoch milliseconds, or 0 when no field holds a date
 * we can parse.
 *
 * Every field is tried in turn rather than taking the first field that merely
 * EXISTS — QuickBooks returns `""` for a cleared date, and `??` would accept
 * that empty string and never reach `DueDate`.
 */
export function invoiceTimestamp(item?: InvoiceListItem): number {
  const data = item?.data;
  if (!data) return 0;

  for (const field of DATE_FIELDS) {
    const value = data[field];
    if (typeof value !== "string" || value.trim() === "") continue;

    const timestamp = new Date(value).getTime();
    if (!Number.isNaN(timestamp)) return timestamp;
  }

  return 0;
}

/**
 * Newest first. Undated invoices sort last (timestamp 0), and ties keep their
 * incoming order — `Array.prototype.sort` is stable, so an unordered QuickBooks
 * response never reshuffles between renders.
 */
export function sortInvoicesNewestFirst<T extends InvoiceListItem>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => invoiceTimestamp(b) - invoiceTimestamp(a));
}
